import { cq } from '../../common/containerQuery'

const sizes = {
  md: {
    control: { w: 4, h: 4 },
    label: { fontSize: 'md' }
  },
  lg: {
    control: { w: 6, h: 6 },
    label: { fontSize: 'md' }
  }
}

function focusRing(color = 'transparent') {
  // If the color is transparent, we don't want to show the white ring
  const innerRing = `0px 0px 0px 2px ${color === 'transparent' ? 'transparent' : 'white'}`
  const outerRing = `0px 0px 0px 4px ${color}`
  return `${innerRing}, ${outerRing}`
}

const baseStyleControl = {
  bg: 'var(--vndly-color-input-background)',
  borderColor: 'var(--vndly-color-input-border)',
  borderWidth: '1px',
  w: '1.5rem',
  h: '1.5rem',
  boxShadow: focusRing('transparent'),

  // Hover Gray Circle
  '&:not([data-disabled], [data-readonly])': {
    _after: {
      content: "''",
      boxShadow: '0 0 0 7px var(--vndly-color-input-shadow-hover)',
      position: 'absolute',
      borderRadius: '9999px',
      width: '24px',
      height: '24px',
      zIndex: '-1',
      transition: 'transform 0.15s ease-out, opacity 0.15s ease-out',
      opacity: 0,
      transform: 'scale(0.7)'
    },
    _hover: {
      _after: {
        opacity: 1,
        transform: 'scale(1)'
      }
    }
  },

  _focusVisible: {
    '&:not([data-checked], [data-invalid], [data-warned])': {
      borderWidth: '2px',
      borderColor: 'var(--vndly-color-input-border-focused)',
      boxShadow: focusRing('transparent')
    }
  },
  _checked: {
    color: 'white',
    bg: 'var(--vndly-color-input-background-checked)',
    borderWidth: '2px',
    borderColor: 'var(--vndly-color-input-border-checked)',
    _focusVisible: {
      boxShadow: focusRing('var(--vndly-color-input-border-checked)'),
      '&[data-hover]:not([data-disabled])': {
        boxShadow: focusRing('var(--vndly-color-input-border-checked)')
      }
    },
    '&[data-hover]:not([data-disabled], [data-invalid], [data-warned], [data-readonly])': {
      bg: 'var(--vndly-color-input-border-checked)',
      borderColor: 'var(--vndly-color-input-border-checked)'
    }
  },
  '&[data-disabled]': {
    cursor: 'not-allowed',
    boxShadow: focusRing('transparent'),
    color: 'var(--vndly-color-input-background-disabled)',
    bg: 'var(--vndly-color-input-background-disabled)',
    borderColor: 'var(--vndly-color-input-border-disabled)',
    '&[data-checked]': {
      borderColor: 'var(--vndly-color-input-border-checked-disabled)',
      background: 'var(--vndly-color-input-background-checked-disabled)',
      color: 'var(--vndly-color-input-icon-checked)'
    }
  },
  '&[data-readonly]': {
    cursor: 'default',
    '&:not([data-checked])': {
      bg: 'var(--vndly-color-input-background-disabled)',
      '&[data-hover]:not([data-checked])': {
        bg: 'var(--vndly-color-input-background-disabled)',
        borderColor: 'var(--vndly-color-input-border)'
      }
    },
    '&[data-checked][data-hover]': {
      bg: 'var(--vndly-color-input-background-checked)',
      borderColor: 'var(--vndly-color-input-border-checked)',
      color: 'white'
    }
  },
  '&[data-invalid]': makeInvalidControlStyles('invalid'),
  '&[data-warned]:not([data-invalid])': makeInvalidControlStyles('warned')
}

const baseStyleLabel = {
  'label:has(input[readonly]) > &': {
    cursor: 'default'
  }
}

const baseStyleContainer = {
  '&:has(input[readonly])': {
    cursor: 'default'
  }
}

/**
 * @param variant {"invalid" | "warned"}
 */
function makeInvalidControlStyles(variant) {
  return {
    borderWidth: '2px',
    borderColor: `var(--vndly-color-input-border-${variant})`,
    _hover: {
      borderWidth: '2px',
      borderColor: `var(--vndly-color-input-border-${variant})`
    },

    _focusVisible: {
      borderWidth: '2px',
      borderColor: 'var(--vndly-color-input-border-focused)',
      boxShadow: focusRing('transparent'),
      '&[data-hover]:not([data-disabled])': {
        boxShadow: focusRing('transparent')
      }
    },
    '&[data-disabled]': {
      cursor: 'not-allowed',
      boxShadow: focusRing('transparent'),
      color: 'var(--vndly-color-input-background-disabled)',
      bg: 'var(--vndly-color-input-background-disabled)',
      borderColor: `var(--vndly-color-input-border-disabled)`,
      '&[data-checked]': {
        borderColor: 'var(--vndly-color-input-border-checked-disabled)',
        background: 'var(--vndly-color-input-background-checked-disabled)',
        color: 'var(--vndly-color-input-icon-checked)'
      }
    },
    _checked: {
      bg: `var(--vndly-color-input-background-checked)`,
      borderColor: `var(--vndly-color-input-border-checked)`,

      boxShadow: focusRing(`var(--vndly-color-input-shadow-${variant})`),

      _focusVisible: {
        boxShadow: focusRing(`var(--vndly-color-input-shadow-focused)`),
        '&[data-hover]:not([data-disabled])': {
          boxShadow: focusRing(`var(--vndly-color-input-shadow-focused)`)
        }
      }
    }
  }
}

const variantSegmentedControl = props => {
  return cq({
    customLabel: {
      flex: props.isFullWidth ? '1 1 0' : 'none'
    },
    customControl: {
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
      justifyContent: props.isFullWidth
        ? { '@segmentedControl/base': 'flex-start', [`@segmentedControl/${props.bp}`]: 'center' }
        : 'center',
      textStyle: 'body',
      cursor: 'pointer',
      p: 3,
      h: 10,
      borderWidth: '1px',
      borderColor: 'gray.300',
      transitionProperty: 'var(--chakra-transition-property-common)',
      transitionDuration: 'var(--chakra-transition-duration-normal)',
      whiteSpace: props.isFullWidth
        ? { '@segmentedControl/base': 'nowrap', [`@segmentedControl/${props.bp}`]: 'initial' }
        : 'initial',
      _hover: {
        bgColor: 'gray.200'
      },
      _active: {
        bgColor: 'gray.200'
      },
      _checked: {
        bgColor: 'var(--vndly-color-button-background-primary)',
        color: 'var(--vndly-color-button)',
        borderColor: 'var(--vndly-color-button-background-primary)',
        zIndex: 1,
        fontWeight: 'semibold',
        textStyle: 'bodySemi'
      },
      _focusVisible: {
        boxShadow: 'outline',
        zIndex: 1
      },
      ...(props.isFullWidth
        ? {
            'label:not(:first-of-type) > &': {
              borderLeftWidth: { '@segmentedControl/base': '1px', [`@segmentedControl/${props.bp}`]: 0 },
              borderTopWidth: { '@segmentedControl/base': 0, [`@segmentedControl/${props.bp}`]: '1px' },
              _checked: {
                borderLeftWidth: '1px',
                borderTopWidth: '1px'
              }
            },
            'label:first-of-type > &': {
              roundedTopRight: { '@segmentedControl/base': 'base', [`@segmentedControl/${props.bp}`]: 'none' },
              roundedTopLeft: { '@segmentedControl/base': 'base', [`@segmentedControl/${props.bp}`]: 'base' },
              roundedBottomLeft: { '@segmentedControl/base': 'none', [`@segmentedControl/${props.bp}`]: 'base' }
            },
            'label:last-of-type > &': {
              roundedTopRight: { '@segmentedControl/base': 'none', [`@segmentedControl/${props.bp}`]: 'base' },
              roundedBottomRight: { '@segmentedControl/base': 'base', [`@segmentedControl/${props.bp}`]: 'base' },
              roundedBottomLeft: { '@segmentedControl/base': 'base', [`@segmentedControl/${props.bp}`]: 'none' }
            },
            // If an unchecked tab is followed by a checked tab, remove the right border
            'label:has(+ label:is([data-checked])) > &': {
              borderRightWidth: { '@segmentedControl/base': '1px', [`@segmentedControl/${props.bp}`]: 0 },
              borderBottomWidth: { '@segmentedControl/base': 0, [`@segmentedControl/${props.bp}`]: '1px' }
            }
          }
        : {
            'label:not(:first-of-type) > &': {
              borderLeftWidth: 0,
              _checked: {
                borderLeftWidth: '1px'
              }
            },
            'label:first-of-type > &': {
              roundedLeft: 'base'
            },
            'label:last-of-type > &': {
              roundedRight: 'base'
            },
            // If an unchecked tab is followed by a checked tab, remove the right border
            'label:has(+ label:is([data-checked])) > &': {
              borderRightWidth: 0
            }
          })
    }
  })
}

export const Radio = {
  baseStyle: {
    container: baseStyleContainer,
    control: baseStyleControl,
    label: baseStyleLabel
  },
  sizes,
  defaultProps: {
    size: 'lg'
  },
  /**
   * Here's how you can create custom variants:
   * 1. Add a key to the variants object
   * 2. Add a customLabel and customControl key to the variant object
   * 3. Add styles to the customLabel and customControl object
   *   - customLabel styles are applied to the label container
   *   - customControl styles are applied to the custom checkbox within
   * 4. Use the variant prop to apply the custom styles
   */
  variants: {
    segmentedControl: variantSegmentedControl
  }
}
